import React from 'react';
import { Link } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL, SITE_URL } from '../../constants/brand';
import { CORE_CITIES } from '../../data/cities';
import type { ServiceGuide } from '../../data/serviceGuides';
import { NAV_SERVICES, getService } from '../../data/services';
import { locationPath, ROUTES, serviceCityPath, servicePath } from '../../enum/routes';
import CtaBand from '../CtaBand';
import FaqSection from '../FaqSection';
import Seo from '../Seo';

export default function DeepServicePage({ guide }: { guide: ServiceGuide }) {
  const service = getService(guide.slug)!;
  const path = servicePath(guide.slug);
  const related = NAV_SERVICES.filter((s) => s.slug !== guide.slug).slice(0, 6);
  const content = guide;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${service.name} in Texas`,
    description: content.metaDescription,
    provider: {
      '@type': 'Organization',
      name: 'Epic Home Pros',
      url: SITE_URL,
      telephone: PHONE_TEL,
    },
    areaServed: CORE_CITIES.map((c) => ({
      '@type': 'City',
      name: c.name,
      containedInPlace: { '@type': 'State', name: 'Texas' },
    })),
    url: `${SITE_URL}${path}`,
  };

  return (
    <>
      <Seo
        title={content.metaTitle}
        description={content.metaDescription}
        path={path}
        jsonLd={[serviceSchema, faqSchema]}
      />

      <section className="relative isolate flex min-h-[60vh] w-full items-center overflow-hidden md:min-h-[68vh]">
        <img alt={service.heroImageAlt} className="absolute inset-0 h-full w-full object-cover" src={service.heroImage} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-black/70" aria-hidden="true" />
        <div className="container-page relative z-10 py-20 text-white">
          <div className="max-w-3xl">
            <div className="label-eyebrow !text-white/90">{content.hero.eyebrow}</div>
            <h1 className="h-display mt-4 text-white drop-shadow">{content.hero.title}</h1>
            <p className="mt-5 max-w-2xl text-base text-white/90 sm:text-lg">{content.hero.lead}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link className="btn-primary" to={ROUTES.QUOTE}>
                Get a Free Quote
              </Link>
              <a
                href={`tel:${PHONE_TEL}`}
                className="btn-secondary border-white bg-white/10 text-white hover:bg-white hover:text-forest"
              >
                Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.overview.title}</h2>
            <div className="mt-6 space-y-4">
              {content.overview.paragraphs.map((p) => (
                <p key={p.slice(0, 48)} className="leading-relaxed text-ink/80">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.cost.title}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.cost.intro}</p>
            <p className="mt-3 text-sm leading-relaxed text-ink/65">{content.cost.disclaimer}</p>
            <div className="mt-8 overflow-x-auto">
              <table className="w-full min-w-[36rem] text-left text-sm">
                <thead>
                  <tr className="border-b border-black/10 text-xs uppercase tracking-wider text-ink/60">
                    <th className="py-3 pr-4 font-semibold">Tier</th>
                    <th className="py-3 pr-4 font-semibold">2026 planning range</th>
                    <th className="py-3 font-semibold">Typical scope</th>
                  </tr>
                </thead>
                <tbody>
                  {content.cost.rows.map((row) => (
                    <tr key={row.tier} className="odd:bg-white even:bg-forest-tint/40">
                      <td className="py-3 pr-4 font-medium text-ink">{row.tier}</td>
                      <td className="py-3 pr-4 font-semibold text-forest">{row.cost}</td>
                      <td className="py-3 text-ink/75">{row.includes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h3 className="mt-12 font-serif text-xl leading-snug text-ink sm:text-2xl">{content.cost.factorsTitle}</h3>
            <p className="mt-3 leading-relaxed text-ink/80">{content.cost.factorsIntro}</p>
            <ul className="mt-5 list-disc space-y-2 pl-5 text-ink/80 marker:text-forest">
              {content.cost.factors.map((f) => (
                <li key={f} className="leading-relaxed">
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.breakdown.title}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.breakdown.intro}</p>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {content.breakdown.items.map((item) => (
              <li
                key={item.title}
                className="card-hover rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
              >
                <h3 className="font-serif text-xl text-ink">{item.title}</h3>
                <p className="mt-1 text-sm font-semibold text-forest">{item.range}</p>
                <p className="mt-2 text-sm leading-relaxed text-ink/75">{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CtaBand title={content.midCta.title} lead={content.midCta.lead} />

      <section className="bg-forest-tint py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.materials.title}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.materials.intro}</p>
          </div>
          <div className="mt-10 space-y-12">
            {content.materials.groups.map((group) => (
              <div key={group.title}>
                <h3 className="font-serif text-xl text-ink sm:text-2xl">{group.title}</h3>
                <p className="mt-2 max-w-3xl leading-relaxed text-ink/75">{group.intro}</p>
                <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                  {group.options.map((opt) => (
                    <li key={opt.name} className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <h4 className="font-semibold text-ink">{opt.name}</h4>
                        <span className="text-sm font-medium text-forest">{opt.range}</span>
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-ink/75">{opt.note}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.timelines.title}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.timelines.intro}</p>
            <div className="mt-8 overflow-x-auto">
              <table className="w-full min-w-[32rem] text-left text-sm">
                <thead>
                  <tr className="border-b border-black/10 text-xs uppercase tracking-wider text-ink/60">
                    <th className="py-3 pr-4 font-semibold">Project type</th>
                    <th className="py-3 pr-4 font-semibold">Approximate duration</th>
                    <th className="py-3 font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {content.timelines.rows.map((row) => (
                    <tr key={row.tier} className="odd:bg-white even:bg-cream/80">
                      <td className="py-3 pr-4 font-medium">{row.tier}</td>
                      <td className="py-3 pr-4 font-semibold text-forest">{row.duration}</td>
                      <td className="py-3 text-ink/75">{row.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h3 className="mt-12 font-serif text-xl text-ink sm:text-2xl">{content.timelines.delaysTitle}</h3>
            <ul className="mt-5 list-disc space-y-2 pl-5 text-ink/80 marker:text-forest">
              {content.timelines.delays.map((d) => (
                <li key={d} className="leading-relaxed">
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.included.title}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.included.intro}</p>
            <ul className="mt-8 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {content.included.checklist.map((item) => (
                <li key={item} className="flex items-start gap-2 text-ink/80">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-forest" aria-hidden="true" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
            <ul className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
              {content.included.groups.map((group) => (
                <li key={group.title} className="rounded-xl border border-black/10 bg-white p-5 shadow-sm">
                  <h3 className="font-serif text-xl text-ink">{group.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/75">{group.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.projectTypes.title}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.projectTypes.intro}</p>
          </div>
          <ul className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2">
            {content.projectTypes.items.map((item) => (
              <li
                key={item.title}
                className="card-hover rounded-xl border border-black/10 bg-white p-6 shadow-sm hover:border-forest/40"
              >
                <h3 className="font-serif text-xl text-ink">{item.title}</h3>
                <p className="mt-2 leading-relaxed text-ink/75">{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-forest-tint py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.process.title}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.process.intro}</p>
            <ol className="mt-8 space-y-4">
              {content.process.steps.map((step) => (
                <li key={step.label} className="rounded-lg border border-black/5 bg-white p-5 shadow-sm">
                  <div className="text-xs font-semibold uppercase tracking-wider text-forest">{step.label}</div>
                  <p className="mt-2 text-ink/80">{step.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.considerations.title}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.considerations.intro}</p>
            <ul className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {content.considerations.items.map((item) => (
                <li
                  key={item.title}
                  className="card-hover rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
                >
                  <h3 className="font-semibold text-ink">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/75">{item.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <div className="mx-auto max-w-4xl">
            <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{content.local.title}</h2>
            <p className="mt-4 leading-relaxed text-ink/80">{content.local.intro}</p>
            <div className="mt-8 space-y-6">
              {content.local.cities.map((city) => (
                <div key={city.slug}>
                  <h3 className="font-serif text-xl text-ink">
                    <Link
                      className="text-forest hover:text-forest-hover hover:underline"
                      to={serviceCityPath(guide.slug, city.slug)}
                    >
                      {service.name} in {city.name}, TX
                    </Link>
                  </h3>
                  <p className="mt-2 leading-relaxed text-ink/80">{city.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 leading-relaxed text-ink/80">{content.local.closing}</p>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20">
        <div className="container-page">
          <div className="mb-10">
            <div className="label-eyebrow">{content.gallery.eyebrow}</div>
            <h2 className="h-section mt-3">{content.gallery.title}</h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {content.gallery.images.map((img) => (
              <div key={img.src} className="img-zoom overflow-hidden rounded-lg">
                <img className="img-zoom-target aspect-[4/3] w-full object-cover" alt={img.alt} src={img.src} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20">
        <div className="container-page">
          <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">{service.name} by city</h2>
          <p className="mt-3 max-w-2xl text-ink/75">
            Dedicated pages for Austin, Dallas, Houston, and San Antonio.
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {CORE_CITIES.map((city) => (
              <li key={city.slug}>
                <Link
                  className="card-hover block rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
                  to={serviceCityPath(guide.slug, city.slug)}
                >
                  <span className="font-serif text-xl text-ink">
                    {city.name}, {city.state}
                  </span>
                  <span className="mt-2 block text-sm font-medium text-forest">
                    {service.name} in {city.name} →
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm">
            <Link className="font-medium text-forest hover:underline" to={ROUTES.SERVICE_AREAS}>
              Service Areas
            </Link>
            {' · '}
            <Link className="font-medium text-forest hover:underline" to={locationPath('fort-worth')}>
              Fort Worth
            </Link>
            {' · '}
            <Link className="font-medium text-forest hover:underline" to={locationPath('the-woodlands')}>
              The Woodlands
            </Link>
          </p>
        </div>
      </section>

      <section className="bg-forest-tint py-16 sm:py-20">
        <div className="container-page">
          <h2 className="font-serif text-2xl leading-snug text-ink sm:text-3xl">Related services</h2>
          <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {related.map((s) => (
              <li key={s.slug}>
                <Link
                  className="block rounded-xl border border-black/10 bg-white p-5 shadow-sm hover:border-forest/40"
                  to={servicePath(s.slug)}
                >
                  <span className="font-serif text-lg text-ink">{s.name}</span>
                  <span className="mt-1 block text-sm text-forest">Learn more →</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <FaqSection title={`${service.name} FAQs`} items={[...content.faqs]} />
      <CtaBand title={content.finalCta.title} lead={content.finalCta.lead} />
    </>
  );
}
